import * as amplitude from '@amplitude/analytics-browser';
function readOptanonConsentCookie() {
    try {
        if (typeof document === 'undefined') return null;
        const cookie = document.cookie.split('; ').find((c)=>c.startsWith('OptanonConsent='));
        if (!cookie) {
            return null;
        }
        const value = cookie.replace('OptanonConsent=', '');
        return value;
    } catch (error) {
        return null;
    }
}
function readOneTrustActiveGroups() {
    try {
        if (typeof window === 'undefined') return null;
        const value = window.OnetrustActiveGroups;
        if (typeof value === 'string' && value.trim().length > 0) {
            return value;
        }
        return null;
    } catch  {
        return null;
    }
}
function setConsentIdentityProperty(consent) {
    try {
        const identify = new amplitude.Identify();
        identify.set('client_cookie_consent', String(consent ?? 'null'));
        amplitude.identify(identify);
    } catch  {
    // best-effort
    }
}
function setConsentIdIdentityProperty(consentId) {
    try {
        const identify = new amplitude.Identify();
        identify.set('consent_id', String(consentId ?? 'null'));
        amplitude.identify(identify);
    } catch  {
    // best-effort
    }
}
function parseFlags(consent) {
    const set = new Set();
    if (!consent) return set;
    // Normalize: ensure leading/trailing commas don't produce empty tokens
    consent.split(',').map((t)=>t.trim()).filter(Boolean).forEach((t)=>set.add(t.toLowerCase()));
    return set;
}
function readGeoCountryFromOneTrust() {
    try {
        if (typeof window === 'undefined') return null;
        const ot = window.OneTrust;
        const geo = ot?.getGeolocationData?.();
        const country = geo?.country || geo?.Country || geo?.countryCode;
        if (typeof country === 'string' && country.length > 0) {
            return country.toUpperCase();
        }
        return null;
    } catch  {
        return null;
    }
}
function readGeoCountryFromOptanonCookie() {
    try {
        if (typeof document === 'undefined') return null;
        const cookie = document.cookie.split('; ').find((c)=>c.startsWith('OptanonConsent='));
        if (!cookie) return null;
        const value = decodeURIComponent(cookie.split('=')[1] || '');
        const match = value.match(/geolocation=([^&;]+)/);
        if (!match) return null;
        const raw = decodeURIComponent(match[1]);
        if (raw.startsWith('{')) {
            // JSON format {"country":"US", ...}
            try {
                const obj = JSON.parse(raw);
                const country = obj?.country || obj?.Country || null;
                return typeof country === 'string' ? country.toUpperCase() : null;
            } catch  {
                return null;
            }
        }
        // Common format like "US;CA" – first token is country code
        const code = raw.split(';')[0]?.trim();
        return code ? code.toUpperCase() : null;
    } catch  {
        return null;
    }
}
function computeRegion(countryCode) {
    if (!countryCode) return 'ROW';
    const EU_EEA_UK = new Set([
        'AT',
        'BE',
        'BG',
        'HR',
        'CY',
        'CZ',
        'DK',
        'EE',
        'FI',
        'FR',
        'DE',
        'GR',
        'HU',
        'IE',
        'IT',
        'LV',
        'LT',
        'LU',
        'MT',
        'NL',
        'PL',
        'PT',
        'RO',
        'SK',
        'SI',
        'ES',
        'SE',
        'IS',
        'LI',
        'NO',
        'GB',
        'UK'
    ]);
    if (EU_EEA_UK.has(countryCode)) return 'EU';
    if (countryCode === 'US') return 'Americas';
    return 'ROW';
}
export function deriveOptOutState(consentFlags, countryCode) {
    const region = computeRegion(countryCode);
    const flags = parseFlags(consentFlags);
    // Helper presence check (case-insensitive via parse)
    const has = (k)=>flags.has(k.toLowerCase());
    // EU logic (strict defaults)
    if (region === 'EU') {
        if (flags.size === 0 || !has('snc')) {
            return {
                region,
                optOut: true,
                optOutPrf: true
            };
        }
        let optOut = true;
        let optOutPrf = true;
        if (has('prf')) {
            optOutPrf = false;
        }
        if (has('trg')) {
            optOut = false;
        }
        return {
            region,
            optOut,
            optOutPrf
        };
    }
    // Americas logic (US defaults)
    if (region === 'Americas') {
        if (flags.size === 0 || !has('snc')) {
            return {
                region,
                optOut: false,
                optOutPrf: false
            };
        }
        let optOut = false; // Default to false
        let optOutPrf = false; // Default to false
        if (!has('prf')) {
            optOutPrf = true;
        }
        if (!has('trg')) {
            optOut = true;
        }
        if (!has('SPD_BG')) optOut = true; // additional US-specific flag
        return {
            region,
            optOut,
            optOutPrf
        };
    }
    if (flags.size === 0 || !has('snc')) {
        return {
            region,
            optOut: false,
            optOutPrf: false
        };
    }
    let optOut = false;
    let optOutPrf = false;
    if (!has('prf')) {
        optOutPrf = true;
    }
    if (!has('trg')) {
        optOut = true;
    }
    return {
        region,
        optOut,
        optOutPrf
    };
}
function removeCookiesByPrefix(prefix) {
    try {
        if (typeof document === 'undefined') return;
        const cookies = document.cookie.split('; ');
        cookies.forEach((cookie)=>{
            const name = cookie.split('=')[0];
            if (name && name.startsWith(prefix)) {
                // Best-effort deletion
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            }
        });
    } catch  {}
}
export function createPrivacyPlugin(onFirstInit) {
    let lastConsent = null;
    let lastCountry = null;
    let lastOptOut = null;
    let lastConsentId = null;
    let isFirstInit = true;
    const updateConsentState = async ()=>{
        const currentConsent = readOneTrustActiveGroups();
        const currentCountry = readGeoCountryFromOneTrust() || readGeoCountryFromOptanonCookie();
        const currentConsentId = readOptanonConsentCookie();
        if (currentConsent !== lastConsent) {
            lastConsent = currentConsent;
            setConsentIdentityProperty(currentConsent);
        }
        if (currentConsentId !== lastConsentId) {
            lastConsentId = currentConsentId;
            setConsentIdIdentityProperty(currentConsentId);
        }
        if (currentCountry !== lastCountry) {
            lastCountry = currentCountry;
        }
        const derived = deriveOptOutState(currentConsent, currentCountry);
        if (!lastOptOut || derived.optOut !== lastOptOut.optOut || derived.optOutPrf !== lastOptOut.optOutPrf) {
            lastOptOut = {
                optOut: derived.optOut,
                optOutPrf: derived.optOutPrf
            };
            try {
                const identify = new amplitude.Identify();
                identify.set('opt_out', String(derived.optOut));
                identify.set('opt_out_prf', String(derived.optOutPrf));
                amplitude.identify(identify);
            } catch  {}
        }
        if (isFirstInit && onFirstInit) {
            isFirstInit = false;
            try {
                await onFirstInit();
            } catch  {}
        }
    };
    if (typeof window !== 'undefined') {
        window.addEventListener('OneTrustGroupsUpdated', ()=>{
            void updateConsentState();
        });
        if (window.OneTrust || window.OnetrustActiveGroups) {
            void updateConsentState();
        }
    }
    return {
        name: 'privacy-plugin',
        type: 'enrichment',
        setup: async ()=>{},
        execute: async (event)=>{
            try {
                if (!lastConsent?.includes('trg')) {
                    if (event) {
                        if (event.language) event.language = '-';
                        if (event.ip) event.ip = '0.0.0.0';
                        if (event.city) event.city = '-';
                        if (event.dma) event.dma = '-';
                        if (event.region) event.region = '-';
                        if (event.country) event.country = '-';
                    }
                }
                const props = event.event_properties || {};
                if (lastOptOut?.optOut) {
                    if (props && typeof props === 'object') {
                        if (props.hasOwnProperty('language') && props?.language) props.language = '-';
                        if (props.hasOwnProperty('[Amplitude] Language') && props['[Amplitude] Language']) props['[Amplitude] Language'] = '-';
                        if (props.hasOwnProperty('client_ip_address') && props?.client_ip_address) props.client_ip_address = '-';
                        if (props.hasOwnProperty('[Amplitude] City') && props['[Amplitude] City']) props['[Amplitude] City'] = '-';
                        if (props.hasOwnProperty('[Amplitude] DMA') && props['[Amplitude] DMA']) props['[Amplitude] DMA'] = '-';
                        if (props.hasOwnProperty('[Amplitude] Region') && props['[Amplitude] Region']) props['[Amplitude] Region'] = '-';
                        if (props.hasOwnProperty('[Amplitude] Country') && props['[Amplitude] Country']) props['[Amplitude] Country'] = '-';
                    }
                    if (event) {
                        if (event.language) event.language = '-';
                        if (event.ip) event.ip = '0.0.0.0';
                        if (event.city) event.city = '-';
                        if (event.dma) event.dma = '-';
                        if (event.region) event.region = '-';
                        if (event.country) event.country = '-';
                    }
                }
                if (lastOptOut?.optOutPrf) {
                    if (props && typeof props === 'object') {
                        if (props.hasOwnProperty('url') && props?.url) props.url = '-';
                        if (props.hasOwnProperty('[Amplitude] Page URL') && props['[Amplitude] Page URL']) props['[Amplitude] Page URL'] = '-';
                        if (props.hasOwnProperty('page_url') && props?.page_url) props.page_url = '-';
                        // Remove Facebook tracking parameters - always delete regardless of value
                        if (props.hasOwnProperty('fbclid')) delete props.fbclid;
                        if (props.hasOwnProperty('fbc')) delete props.fbc;
                        if (props.hasOwnProperty('fbp')) delete props.fbp;
                    }
                    const userProps = event.user_properties || {};
                    if (userProps && typeof userProps === 'object') {
                        const initialParamsToMask = [
                            'initial_dclid',
                            'initial_fbclid',
                            'initial_gbraid',
                            'initial_gclid',
                            'initial_ko_click_id',
                            'initial_li_fat_id',
                            'initial_msclkid',
                            'initial_referrer',
                            'initial_referring_domain',
                            'initial_rtd_cid',
                            'initial_ttclid',
                            'initial_twclid',
                            'initial_utm_campaign',
                            'initial_utm_content',
                            'initial_utm_id',
                            'initial_utm_medium',
                            'initial_utm_source',
                            'initial_utm_term',
                            'initial_wbraid'
                        ];
                        initialParamsToMask.forEach((param)=>{
                            if (userProps.hasOwnProperty(param)) {
                                userProps[param] = '-';
                            }
                        });
                        event.user_properties = userProps;
                    }
                    removeCookiesByPrefix('AMP_MKTG_');
                }
                event.event_properties = props;
            } catch  {}
            return event;
        }
    };
}
